import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { APP_LINKS } from 'src/config/app-links.config';
import { useArchiveProgramPlan } from 'src/api/mutations/useArchiveProgramPlan';
import { useDeleteProgramPlan } from 'src/api/mutations/useDeleteProgramPlan';
import { useSendProgramPlanToClient } from 'src/api/mutations/useSendProgramPlanToClient';
import { useDuplicateProgramPlan } from 'src/api/mutations/useDuplicateProgramPlan';
import { AssignToClientModal } from '../../../ProgramGenerated/components/AssignToClientModal/AssignToClientModal';
import { Button } from 'src/components/Button/Button';
import { sessionStore } from 'src/store/sessionStore';
import './ProgramCard.scss';
import { ReactComponent as ProgramAI } from '../../../../../../../../assets/images/onboarding-trainer/dashboard/programs/program-ai.svg';
import { ReactComponent as ProgramAILarge } from '../../../../../../../../assets/images/onboarding-trainer/dashboard/programs/program-ai-large.svg';
import { ReactComponent as ProgramShare } from '../../../../../../../../assets/images/onboarding-trainer/dashboard/programs/share.svg';
import { ReactComponent as ProgramArchive } from '../../../../../../../../assets/images/onboarding-trainer/dashboard/programs/archive.svg';
import { ReactComponent as ProgramArchiveLarge } from '../../../../../../../../assets/images/onboarding-trainer/dashboard/programs/archive-confirmation.svg';
import { ReactComponent as MoreProgramWorkoutIcon } from 'src/assets/images/trainer/clients/more_program_workout.svg';
import { ReactComponent as CalendarIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/calendar.svg';
import { ReactComponent as ClockIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/clock.svg';
import { ReactComponent as TrashIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/delete.svg';
import { ReactComponent as AssignToClientIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/assign-to-client.svg';
import { ReactComponent as CopyIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/copy.svg';
import { ReactComponent as DashboardModalDeleteIcon } from 'src/assets/images/trainer/clients/dashboard-modal-delete.svg';
import { ReactComponent as DashboardModalDuplicateIcon } from 'src/assets/images/trainer/clients/dashboard-modal-duplicate.svg';

// Modal Images
import { ReactComponent as CopyLink } from 'src/assets/images/onboarding-trainer/dashboard/programs/copy_link.svg';
import { ReactComponent as MiniPTApp } from 'src/assets/images/onboarding-trainer/dashboard/programs/minipt-app.svg';
import { ReactComponent as Messenger } from 'src/assets/images/onboarding-trainer/dashboard/programs/messenger.svg';
import { ReactComponent as SMS } from 'src/assets/images/onboarding-trainer/dashboard/programs/sms.svg';
import { ReactComponent as CloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/close-icon.svg';
export interface ProgramData {
  status?: 'assigned' | 'unassigned' | 'draft';
  id: string;
  docPath: string;
  isSent: boolean;
  clientId: string;
  generatorType?: string;
  isManually?: boolean;
  programmeName: string;
  weeks: number;
  daysPerWeek: number;
  tags: string;
  thumbnails: string[];
  nextStepSlug?: string;
  assignedTo: {
    name: string;
    initials: string;
  };
  date: string;
  isActive: string;
  createTime: string;
}

interface ProgramCardProps {
  data: ProgramData;
  viewMode: 'grid' | 'list';
}

export const ProgramCard: FC<ProgramCardProps> = ({ data, viewMode }) => {
  const navigate = useNavigate();
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid;
  const isAssigned = !!data.clientId;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const toggleShareModal = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsShareModalOpen(!isShareModalOpen);
  };

  const { mutate: archiveProgram, isPending: isArchiving } = useArchiveProgramPlan();
  const { mutate: sendProgram, isPending: isSending } = useSendProgramPlanToClient();

  const isArchived =
    data.isActive === '0' || String(data.isActive) === '0' || data.isActive === 'false';

  const handleArchive = () => {
    const docPath =
      data.docPath ||
      (data.id && trainerId ? `trainer_app_users/${trainerId}/programme_plan/${data.id}` : '');
    archiveProgram(
      { docPath, programId: data.id, trainerId, isActive: isArchived ? '1' : '0' },
      {
        onSuccess: () => {
          setIsArchiveModalOpen(false);
        },
      },
    );
  };

  const { mutate: deleteProgram, isPending: isDeleting } = useDeleteProgramPlan();
  const { mutate: duplicateProgram, isPending: isDuplicating } = useDuplicateProgramPlan();

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDuplicateModalOpen(true);
  };

  const confirmDuplicate = () => {
    const docPath =
      data.docPath ||
      (data.id && trainerId ? `trainer_app_users/${trainerId}/programme_plan/${data.id}` : '');
    duplicateProgram(
      { docPath, programId: data.id },
      {
        onSuccess: () => {
          setIsDuplicateModalOpen(false);
        },
      },
    );
  };

  const handleDelete = () => {
    const docPath =
      data.docPath ||
      (data.id && trainerId ? `trainer_app_users/${trainerId}/programme_plan/${data.id}` : '');
    deleteProgram(
      { docPath, programId: data.id, trainerId },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
        },
      },
    );
  };

  const appStoreLink = APP_LINKS.ios;
  const shareLink = `${window.location.origin}/program/${data.id}`;
  const shareText = `Your PT has sent you “${data.programmeName}”.\nView it in the miniPT app. If you don’t have miniPT yet, install the app first, then open this link:\n${shareLink}\n\nDownload on the App Store:\n${appStoreLink}`;
  const encodedShareText = encodeURIComponent(shareText);

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareText);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedShareText}`, '_blank');
  };

  const handleMessenger = () => {
    window.open(`fb-messenger://share/?link=${encodeURIComponent(shareLink)}`, '_blank');
  };

  const handleSMS = () => {
    window.open(`sms:?body=${encodedShareText}`, '_self');
  };

  const handleMiniPTApp = () => {
    // If no client is assigned yet, open the assign modal first
    if (!data.clientId) {
      setIsShareModalOpen(false);
      setIsAssignModalOpen(true);
      return;
    }
    sendProgram(
      {
        docPath: data.docPath,
        clientId: data.clientId,
        programId: data.id,
        notify: true,
        startDate: new Date().toISOString().split('T')[0],
        message: '',
      },
      {
        onSuccess: () => {
          setIsShareModalOpen(false);
        },
      },
    );
  };

  const isDraft = data.status === 'draft';

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const forceGridOnMobile = true;
  const activeViewMode = forceGridOnMobile && isMobile ? 'grid' : viewMode;
  const hideThumbnails = viewMode === 'list';

  return (
    <div
      className={`program-card-wrapper ${activeViewMode}-view ${hideThumbnails ? 'hide-thumbnails' : ''}`}
      onClick={() => {
        if (isDraft) {
          const genType =
            data.generatorType === 'manual' || (data as any)?.type === 'manual' || data.isManually
              ? 'manual'
              : 'ai';
          navigate(
            `/onboarding-trainer/dashboard/program-generator/${genType}?programId=${data.id}`,
          );
        } else {
          const isManualProg =
            data.generatorType === 'manual' ||
            (data as unknown as Record<string, unknown>)?.type === 'manual' ||
            Boolean(data.isManually);
          navigate(`/onboarding-trainer/dashboard/program-generated/${data.id}`, {
            state: {
              clientId: data.clientId,
              clientName: data.assignedTo?.name,
              isManually: isManualProg,
              generatorType: isManualProg ? 'manual' : 'ai',
            },
          });
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      {activeViewMode === 'grid' && (
        <>
          <div className="card-header">
            <div className="title-section">
              <div className="icon-wrapper">
                <ProgramAI />
              </div>
              <div className="title-text">
                <h3 title={data.programmeName}>{data.programmeName}</h3>
                <span className="date">{data.date}</span>
              </div>
            </div>
            {isDraft ? (
              <div className="draft-header-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  className="icon-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteModalOpen(true);
                  }}
                  disabled={isArchiving}
                >
                  <TrashIcon />
                </button>
                <button
                  className="icon-action-btn archive-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsArchiveModalOpen(true);
                  }}
                  disabled={isArchiving}
                >
                  <ProgramArchive />
                </button>
              </div>
            ) : (
              <button className="share-btn" onClick={toggleShareModal}>
                <ProgramShare />
              </button>
            )}
          </div>

          {!isDraft && (
            <div className="meta-info">
              <div className="meta-item-group">
                <div className="info-item">
                  <CalendarIcon />
                  <span>{data.weeks} weeks</span>
                </div>

                <div className="info-item">
                  <ClockIcon />
                  <span>{data.daysPerWeek} days/week</span>
                </div>
              </div>

              <div className="action-buttons-group" onClick={(e) => e.stopPropagation()}>
                <button
                  className="icon-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteModalOpen(true);
                  }}
                  disabled={isArchiving}
                >
                  <TrashIcon />
                </button>

                <button
                  className="icon-action-btn archive-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsArchiveModalOpen(true);
                  }}
                  disabled={isArchiving}
                >
                  <ProgramArchive />
                </button>
              </div>
            </div>
          )}

          {isDraft ? (
            <div className="draft-state">
              <p>Don&apos;t worry. We saved your answers. Continue from where you left</p>
              <button
                className="full-width-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/onboarding-trainer/dashboard/program-generator/ai?programId=${data.id}&step=${data.nextStepSlug || 'energy-level'}`,
                  );
                }}
              >
                Finish program creation
              </button>
            </div>
          ) : (
            viewMode !== 'list' && (
              <div className="thumbnails">
                {data.thumbnails.map((thumb, index) =>
                  thumb ? (
                    <img
                      key={index}
                      src={thumb}
                      alt={`thumb ${index}`}
                      className="thumbnail-item"
                    />
                  ) : (
                    <div key={index} className="thumbnail-item placeholder" />
                  ),
                )}
                <div className="more-thumbnails">
                  <MoreProgramWorkoutIcon />
                </div>
              </div>
            )
          )}

          {isAssigned ? (
            <div className="assignment">
              <div className="assignment-info">
                <span className="label">Assigned to:</span>
                <div className="assignment-details">
                  <div className="avatar">{data.assignedTo.initials}</div>
                  <span className="name" title={data.assignedTo.name}>
                    {data.assignedTo.name}
                  </span>
                </div>
              </div>
              <button
                className="copy-btn"
                disabled={isDuplicating}
                onClick={handleDuplicate}
                title="Duplicate program"
              >
                <CopyIcon />
              </button>
            </div>
          ) : (
            <div className="assignment unassigned">
              <span className="label">Assigned to:</span>
              <button
                className="full-width-btn assign-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAssignModalOpen(true);
                }}
              >
                <AssignToClientIcon /> Assign to client
              </button>
            </div>
          )}
        </>
      )}

      {activeViewMode === 'list' && (
        <>
          <div className="list-content">
            {/* Mobile Title (visible only on mobile) */}
            <div className="mobile-only mobile-top">
              <div className="title-group">
                <div className="icon-wrapper mobile-only">
                  <ProgramAI />
                </div>
                <div className="title-text">
                  <h3>{data.programmeName}</h3>
                  <span className="date">{data.date}</span>
                </div>
              </div>
            </div>

            <div className="list-row desktop-only">
              {/* Section 1: Content (Title, Meta, Assignment) */}
              <div className="left-content-section">
                <div className="info-column">
                  <div className="title-section">
                    <div className="icon-wrapper large-icon">
                      <ProgramAILarge />
                    </div>
                    <div className="title-text">
                      <h3 title={data.programmeName}>{data.programmeName}</h3>
                      <span className="date">{data.date}</span>
                    </div>
                  </div>
                  {isDraft ? (
                    <p className="draft-text-list">
                      Don&apos;t worry. We saved your answers. Continue from where you left
                    </p>
                  ) : (
                    <div className="meta-info">
                      <div className="meta-item">
                        <CalendarIcon className="small-icon" /> {data.weeks} weeks
                      </div>
                      <div className="meta-item">
                        <ClockIcon className="small-icon" /> {data.daysPerWeek} days/week
                      </div>
                    </div>
                  )}
                </div>

                <div className="assignment-column">
                  {isAssigned ? (
                    <div className="assignment">
                      <div className="assignment-info">
                        <span className="label">Assigned to:</span>
                        <div className="assignment-details">
                          <div className="avatar">{data.assignedTo.initials}</div>
                          <span className="name" title={data.assignedTo.name}>
                            {data.assignedTo.name}
                          </span>
                          <button
                            className="copy-btn"
                            disabled={isDuplicating}
                            onClick={handleDuplicate}
                            title="Duplicate program"
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="assignment unassigned">
                      <span className="label">Assigned to:</span>
                      <button
                        className="full-width-btn assign-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAssignModalOpen(true);
                        }}
                      >
                        <AssignToClientIcon /> Assign to client
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 2: Actions */}
              <div className="right-actions-section">
                <div className="icon-actions">
                  <button
                    className="icon-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDeleteModalOpen(true);
                    }}
                    disabled={isArchiving}
                  >
                    <TrashIcon />
                  </button>
                  <button
                    className="icon-action-btn archive-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsArchiveModalOpen(true);
                    }}
                    disabled={isArchiving}
                  >
                    <ProgramArchive />
                  </button>
                </div>
                {isDraft ? (
                  <button
                    className="full-width-btn finish-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/onboarding-trainer/dashboard/program-generator/ai?programId=${data.id}`,
                      );
                    }}
                  >
                    Finish program creation
                  </button>
                ) : (
                  <button className="share-btn with-text" onClick={toggleShareModal}>
                    <ProgramShare />
                    <span>Share</span>
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Actions & Meta */}
            <div className="mobile-only mobile-bottom">
              <div className="mobile-meta-info">
                {isDraft ? (
                  <p className="draft-text-list">
                    Don&apos;t worry. We saved your answers. Continue from where you left
                  </p>
                ) : (
                  <>
                    <div className="meta-item">
                      <CalendarIcon className="small-icon" />
                      {data.weeks} weeks
                    </div>
                    <div className="meta-item">
                      <ClockIcon className="small-icon" />
                      {data.daysPerWeek} days/week
                    </div>
                  </>
                )}
              </div>
              <div className="assignment-column">
                {isAssigned ? (
                  <div className="assignment">
                    <div className="assignment-info">
                      <span className="label">Assigned to:</span>
                      <div className="assignment-details">
                        <div className="avatar">{data.assignedTo.initials}</div>
                        <span className="name" title={data.assignedTo.name}>
                          {data.assignedTo.name}
                        </span>
                        <button
                          className="copy-btn"
                          disabled={isDuplicating}
                          onClick={handleDuplicate}
                          title="Duplicate program"
                        >
                          <CopyIcon />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="assignment unassigned">
                    <span className="label">Assigned to:</span>
                    <button
                      className="full-width-btn assign-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAssignModalOpen(true);
                      }}
                    >
                      <AssignToClientIcon /> Assign to client
                    </button>
                  </div>
                )}
              </div>
              <div className="divider"></div>
              <div className="action-buttons-group">
                <button
                  className="icon-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteModalOpen(true);
                  }}
                  disabled={isArchiving}
                >
                  <TrashIcon />
                </button>
                <button
                  className="icon-action-btn archive-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsArchiveModalOpen(true);
                  }}
                  disabled={isArchiving}
                >
                  <ProgramArchive />
                </button>
                {isDraft ? (
                  <button
                    className="full-width-btn finish-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/onboarding-trainer/dashboard/program-generator/ai?programId=${data.id}`,
                      );
                    }}
                  >
                    Finish program creation
                  </button>
                ) : (
                  <button className="icon-action-btn with-text" onClick={toggleShareModal}>
                    <ProgramShare />
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()}>
        <Modal
          open={isShareModalOpen}
          footer={null}
          onCancel={() => setIsShareModalOpen(false)}
          centered
          width={500}
          closeIcon={null}
          rootClassName="share-program-modal"
          className="share-program-modal"
          styles={{
            mask: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(6, 7, 35, 0.8)',
            },
          }}
        >
          <div className="share-modal-content">
            <div className="modal-header">
              <h3>Share Program</h3>
              <button className="close-btn" onClick={toggleShareModal}>
                <CloseIcon />
              </button>
            </div>
            <p className="modal-subtitle">
              Share &quot;{data.programmeName}&quot; with your client
            </p>

            <div className="main-options">
              <button
                className={`share-option-btn primary-option ${isCopied ? 'copied-btn' : ''}`}
                onClick={handleCopyLink}
              >
                <div className="icon-box copy-link">
                  {isCopied ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="check-icon"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <CopyLink />
                  )}
                </div>
                <div className="option-text">
                  <span className="option-title">{isCopied ? 'Link Copied!' : 'Copy Link'}</span>
                  <span className="option-desc">
                    {isCopied ? 'Copied to clipboard!' : 'Copy shareable link to clipboard'}
                  </span>
                </div>
              </button>

              <button
                className="share-option-btn primary-option"
                onClick={handleMiniPTApp}
                disabled={isSending}
              >
                <div className="icon-box minipt-app">
                  <MiniPTApp />
                </div>
                <div className="option-text">
                  <span className="option-title">
                    {isSending ? 'Sending...' : data.isSent ? 'Send Again' : 'Send via miniPT App'}
                  </span>
                  <span className="option-desc">
                    {data.isSent
                      ? "Resend and update programme in client's miniPT app"
                      : !data.clientId
                        ? 'Assign a client first to send via miniPT app'
                        : "Send directly to client's miniPT app"}
                  </span>
                </div>
              </button>
            </div>

            <div className="modal-divider">
              <span>Or share via</span>
            </div>

            <div className="secondary-options">
              <button className="share-option-btn secondary-option" onClick={handleWhatsApp}>
                <div className="icon-box social-icon">
                  <Messenger />
                </div>
                <span>WhatsApp</span>
              </button>

              <button className="share-option-btn secondary-option" onClick={handleMessenger}>
                <div className="icon-box social-icon">
                  <Messenger />
                </div>
                <span>Messenger</span>
              </button>

              <button className="share-option-btn secondary-option" onClick={handleSMS}>
                <div className="icon-box social-icon">
                  <SMS />
                </div>
                <span>SMS</span>
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={isArchiveModalOpen}
          footer={null}
          onCancel={() => setIsArchiveModalOpen(false)}
          centered
          width={400}
          closeIcon={null}
          rootClassName="logout-modal"
          className="logout-modal"
          styles={{
            mask: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(6, 7, 35, 0.8)',
            },
          }}
        >
          <div className="logout-modal-content">
            <div className="logout-icon-wrapper">
              <ProgramArchiveLarge />
            </div>
            <h2>{isArchived ? 'Unarchive' : 'Archive'} Program?</h2>
            <p>
              Are you sure you want to {isArchived ? 'unarchive' : 'archive'} &quot;
              {data.programmeName}&quot;?
            </p>
            <div className="logout-actions">
              <Button
                className="btn-logout"
                disabled={isArchiving}
                isLoading={isArchiving}
                onClick={handleArchive}
              >
                {isArchived ? 'Unarchive' : 'Archive'}
              </Button>
              <button className="btn-cancel" onClick={() => setIsArchiveModalOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={isDeleteModalOpen}
          footer={null}
          onCancel={() => setIsDeleteModalOpen(false)}
          centered
          width={400}
          closeIcon={null}
          rootClassName="logout-modal"
          className="logout-modal"
          styles={{
            mask: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(6, 7, 35, 0.8)',
            },
          }}
        >
          <div className="logout-modal-content">
            <div className="logout-icon-wrapper">
              <DashboardModalDeleteIcon />
            </div>
            <h2>Delete Program?</h2>
            <p>Are you sure you want to delete &quot;{data.programmeName}&quot;?</p>
            <div className="logout-actions">
              <Button
                className="btn-logout"
                disabled={isDeleting}
                onClick={handleDelete}
                isLoading={isDeleting}
              >
                Delete
              </Button>
              <Button className="btn-cancel" onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          open={isDuplicateModalOpen}
          footer={null}
          onCancel={() => setIsDuplicateModalOpen(false)}
          centered
          width={400}
          closeIcon={null}
          rootClassName="logout-modal"
          className="logout-modal"
          styles={{
            mask: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(6, 7, 35, 0.8)',
            },
          }}
        >
          <div className="logout-modal-content">
            <div className="logout-icon-wrapper">
              <DashboardModalDuplicateIcon />
            </div>
            <h2>Duplicate Program?</h2>
            <p>Are you sure you want to duplicate &quot;{data.programmeName}&quot;?</p>
            <div className="logout-actions">
              <Button
                className="btn-logout"
                disabled={isDuplicating}
                onClick={confirmDuplicate}
                isLoading={isDuplicating}
              >
                Duplicate
              </Button>
              <Button className="btn-cancel" onClick={() => setIsDuplicateModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        <AssignToClientModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onAssign={(assignData) => {
            return new Promise<void>((resolve, reject) => {
              sendProgram(
                {
                  docPath: data.docPath,
                  programId: data.id,
                  clientId: assignData.clientId,
                  startDate: assignData.startDate,
                  notify: assignData.notify,
                  message: assignData.message,
                },
                {
                  onSuccess: () => {
                    setIsAssignModalOpen(false);
                    resolve();
                  },
                  onError: (error) => {
                    reject(error);
                  },
                },
              );
            });
          }}
        />
      </div>
    </div>
  );
};
